import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { twMerge } from 'tailwind-merge';
import { COLORS } from './button.helper';

/**
 * A button component.
 * @param {{
 *   className?: string,
 *   color?: string,
 *   disabled?: boolean,
 *   icon?: JSX.Element,
 *   label?: string,
 *   loading?: boolean,
 *   trailingIcon?: boolean,
 *   variant?: string,
 * }} props
 * @return {JSX.Element}
 * @constructor
 * @example return (
 *   <Button variant="outlined" color="red">Close</Button>
 * );
 */
const Button = (props) => {
  const {
    children,
    className,
    color = 'green',
    disabled,
    icon,
    label,
    loading,
    trailingIcon = false,
    variant = 'contained',
    ...other
  } = props;

  const link = variant === 'link';
  const contained = variant === 'contained';
  const outlined = variant === 'outlined';

  const [variantClasses, setVariantClasses] = useState('');
  const [disabledClasses, setDisabledClasses] = useState('');
  const [colorClasses, setColorClasses] = useState('');

  const classes = 'leading-sm font-body text-md font-normal relative inline-block overflow-hidden h-auto py-0 px-5 cursor-pointer text-center align-middle rounded';

  useEffect(() => {
    setVariantClasses([
      `${link ? 'hover:underline' : 'hover:no-underline'}`,
      `${(link || outlined) ? 'bg-transparent' : ''}`,
    ].join(' '));
  }, [variant]);

  useEffect(() => {
    setDisabledClasses(`${disabled ? 'bg-opacity-50 cursor-not-allowed' : ''}`);
  }, [disabled]);

  useEffect(() => {
    if (COLORS[color]) {
      setColorClasses([
        `${(contained) ? COLORS[color].bg : COLORS[color].text}`,
        `${outlined ? COLORS[color].outline : ''}`,
        `${(!link && !disabled) ? COLORS[color].hover : ''}`,
      ].join(' '));
    }
  }, [variant, color, disabled]);

  return (
    <button
      aria-label={label || icon?.props.name}
      className={twMerge(classes, variantClasses, disabledClasses, colorClasses, className)}
      disabled={disabled}
      {...other}
    >
      {(icon && !trailingIcon) && <span className="mr-2">{icon}</span>}
      <span>{label}</span>
      {children}
      {(icon && trailingIcon) && <span className="ml-2">{icon}</span>}
      {loading && (
        <svg className="animate-spin ml-1.5 -mt-1 h-5 w-5 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25 stroke-current" cx="12" cy="12" r="10" strokeWidth="4"></circle>
          <path className="opacity-75 fill-current" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      )}
    </button>
  );
};

Button.propTypes = {
  /**
   * The content of the component.
   */
  children: PropTypes.node,
  /**
   * Override or extend the styles applied to the component.
   */
  className: PropTypes.string,
  /**
   * The color of the component.
   * @default 'green'
   */
  color: PropTypes.string,
  /**
   * If `true`, the component is disabled.
   */
  disabled: PropTypes.bool,
  /**
   * The icon to display.
   */
  icon: PropTypes.node,
  /**
   * Text to be shown.
   */
  label: PropTypes.string,
  /**
   * Text to be shown.
   */
  loading: PropTypes.bool,
  /**
   * Whether to show the icon before or after the label.
   */
  trailingIcon: PropTypes.bool,
  /**
   * @ignore
   */
  type: PropTypes.oneOfType([PropTypes.oneOf(['button', 'reset', 'submit']), PropTypes.string]),
  /**
   * The variant to use.
   * @default 'contained'
   */
  variant: PropTypes.oneOfType([
    PropTypes.oneOf(['contained', 'outlined', 'link']),
    PropTypes.string,
  ]),
};

export { Button };
